import axios from "axios";
import { CategoryKeys } from "../enums";
import { Param, PathConstants, URLUtils } from "../router/pathConstants";
import { GoogleCategory, Place } from "../models";
import apiClient from "./api.service";

const ALLAPATTAH_LOCAL_GUIDE_SERVER_URL = process.env.REACT_APP_ALLAPATTAH_LOCAL_GUIDE_SERVER_URL || "";

const constructGetLocalCategoriesURL = (categoryKey: string) => {
  return `${ALLAPATTAH_LOCAL_GUIDE_SERVER_URL}${PathConstants.CATEGORIES_GOOGLE_LOCAL}?localCategoryKey=${categoryKey}`;
};

const constructGetAllCategoryPlacesURL = (categoryKey: string) => {
  return `${ALLAPATTAH_LOCAL_GUIDE_SERVER_URL}${PathConstants.CATEGORY_PLACES_SIMPLE}?categoryKey=${categoryKey}`;
};

const constructGetOmittedCategoriesURL = () => {
  return `${ALLAPATTAH_LOCAL_GUIDE_SERVER_URL}${PathConstants.CATEGORIES_OMITTED}`;
};

const constructGetOmmitedCategoryBusinessesURL = () => {
  return `${ALLAPATTAH_LOCAL_GUIDE_SERVER_URL}${PathConstants.OMITTED_PLACES}`;
};

const constructConnectGoogleCategoryToLocalCategoryURL = (categoryData: {
  localCategoryKey: string;
  googleCategoryId: string;
}) => {
  return `${ALLAPATTAH_LOCAL_GUIDE_SERVER_URL}${PathConstants.CATEGORIES_CONNECT}?localCategoryKey=${categoryData.localCategoryKey}&googleCategoryId=${categoryData.googleCategoryId}`;
};

const constructRemoveCategoryURL = (googleCategoryTypeName: string) => {
  return `${ALLAPATTAH_LOCAL_GUIDE_SERVER_URL}${PathConstants.CATEGORIES_REMOVE}?googleCategoryTypeName=${googleCategoryTypeName}`;
};

const constructGetPlaceDetailsURL = (id: string) => {
  return `${ALLAPATTAH_LOCAL_GUIDE_SERVER_URL}${PathConstants.PLACES_DETAILS_BY_ID}?id=${id}`;
};

const loginAdminURL = () => {
  return `${ALLAPATTAH_LOCAL_GUIDE_SERVER_URL}${PathConstants.AUTH_LOGIN}`;
};

const logoutAdminURL = () => {
  return `${ALLAPATTAH_LOCAL_GUIDE_SERVER_URL}${PathConstants.AUTH_LOGOUT}`;
};

const getAdminProfileURL = () => {
  return `${ALLAPATTAH_LOCAL_GUIDE_SERVER_URL}${PathConstants.AUTH_USER_PROFILE}`;
};

const constructOmitPlaceURL = (placeId: string) => {
  return `${ALLAPATTAH_LOCAL_GUIDE_SERVER_URL}${PathConstants.PLACES_OMIT}?placeId=${placeId}`;
};

/**
 * Gets request path for category
 *
 * @param {string} category category key
 * @returns {string}
 */
const getMarkerRequestPath = (category: string) => {
  switch (category) {
    case CategoryKeys.default_lsd:
      return PathConstants.LSD_SIMPLE;
    default:
      return PathConstants.ALL_SIMPLE;
  }
};

const checkIfOpen = (place: Place): boolean => {
  try {
    const { openingHours } = place;

    if (!openingHours) return false; // Ensure openingHours exists

    const parsedOpeningHours: string[] = JSON.parse(openingHours); // Parse JSON string
    const newDate = new Date();
    const miamiTimeString = newDate.toLocaleString('en-US', {
      timeZone: 'America/New_York',
    });


    // Create a new Date object from the Miami time string
    const now = new Date(miamiTimeString);

    // Get current day of the week (e.g., "Monday")
    const today = new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: 'America/New_York' }).format(now);

    // Find today's schedule
    const todayEntry = parsedOpeningHours.find((entry: string) => entry.startsWith(today));

    // If closed today, return false
    if (!todayEntry || todayEntry.toLowerCase().includes("closed")) {
      return false;
    }

    if (todayEntry.toLowerCase().includes('open 24 hours')) {
      return true;
    }

    // Extract opening and closing times (handle Unicode spaces)
    const timeRange = todayEntry.split(": ")[1].replace(/\u202F/g, ' ').trim(); // Fix Unicode spaces
    const [openTimeStr, closeTimeStr] = timeRange.split("–").map(s => s.trim());

    // Function to parse time into total minutes for easy comparison
    const parseTime = (timeStr: string): number => {
      const [time, modifier] = timeStr.split(" ");
      let [hours, minutes] = time.split(":").map(Number);

      if (modifier === "PM" && hours !== 12) hours += 12;
      if (modifier === "AM" && hours === 12) hours = 0;

      return hours * 60 + minutes; // Convert to total minutes
    };

    const openMinutes = parseTime(openTimeStr);
    const closeMinutes = parseTime(closeTimeStr);
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    // Check if time range spans over midnight
    if (closeMinutes < openMinutes) {
      // The place closes after midnight (e.g., 9 AM - 3 AM next day)
      return (
        (currentMinutes >= openMinutes && currentMinutes < 1440) || // Open today (9 AM - midnight)
        (currentMinutes >= 0 && currentMinutes < closeMinutes) // Open the next day (midnight - 3 AM)
      );
    }

    return currentMinutes >= openMinutes && currentMinutes <= closeMinutes;
  } catch (e) {
    console.error('Unable to determine opening hours.')
    return false;
  }
}

const AllapattahLocalGuideService = {
  /**
   * Logs in the admin
   *
   * @param {string} email admin email
   * @param {string} password admin password
   * @returns {string}
   */
  loginAdmin: async (email: string, password: string) => {
    const requestURL = loginAdminURL();

    try {
      const response = await axios.post(requestURL, { email, password });
      if (response.data.accessToken) {
        window.localStorage.setItem("token", response.data.accessToken);
      } else {
        throw new Error("Invalid credentials");
      }
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  },

  /**
   * Logs out the admin
   *
   * @returns {string}
   */
  logoutAdmin: async () => {
    const requestURL = logoutAdminURL();
    const response = await axios.post(requestURL);
    return response.data;
  },

  /**
   * Get admin session
   *
   * @returns {string}
   */
  getAdminProfile: async (setData: any) => {
    const requestURL = getAdminProfileURL();

    try {
      console.log("REQUEST URL", requestURL);
      const response = await apiClient.get(requestURL);
      setData(response.data);
    } catch (error) {
      setData(null);
    }
  },

  /**
   * Gets marker data based on open category
   * and updates the component state with the data
   *
   * @param category category id
   * @param setData useState setter
   */
  getMarkersAndUpdateState: (category: string, setData: any) => {
    const path = getMarkerRequestPath(category);
    const params =
      category && category !== CategoryKeys.default
        ? [new Param("categoryKey", category)]
        : undefined;
    const requestURL = URLUtils.createRequestUrl(
      ALLAPATTAH_LOCAL_GUIDE_SERVER_URL,
      path,
      params
    );

    axios
      .get(requestURL)
      .then((res: any) => {
        setData(res.data);
      })
      .catch(() => {
        setData([]);
      });
  },

  /**
   * Gets place data based on place ID
   * and updates the component state with the data
   *
   * @param placeId Google API place ID
   * @param setData useState setter
   */
  getPlaceDetailsAndUpdateState: (placeId: string, setData: any) => {
    const requestURL = URLUtils.createRequestUrl(
      ALLAPATTAH_LOCAL_GUIDE_SERVER_URL,
      PathConstants.PLACES_DETAILS,
      [new Param("placeId", placeId)]
    );

    axios
      .get(requestURL)
      .then((res: any) => {
        const isOpen = res.data ? checkIfOpen(res.data) : false;
        res.data.isOpen = isOpen;

        setData(res.data);
      })
      .catch(() => {
        setData({
          id: placeId,
          title: "There was an error while fetching data",
          rating: 0,
          isOpen: false,
          isLittleSantoDomingo: false,
        });
      });
  },

  /**
   * Gets weather data
   * and updates the component state with the data
   *
   * @param setData useState setter
   */
  getWeatherDataAndUpdateState: (setData: any) => {
    const requestURL = URLUtils.createRequestUrl(
      ALLAPATTAH_LOCAL_GUIDE_SERVER_URL,
      PathConstants.WEATHER_CURRENT
    );
    axios.get(requestURL).then((res: any) => {
      setData(res.data.data);
    });
  },

  /**
   * Gets local categories data based on category key
   * and updates the component state with the data
   *
   * @param categoryKey Category key to fetch
   * @param setData useState setter
   */
  getLocalCategories: (categoryKey: string, setData: any) => {
    const requestURL = constructGetLocalCategoriesURL(categoryKey);

    axios
      .get(requestURL)
      .then((res: any) => {
        setData(res.data);
      })
      .catch(() => {
        setData([]);
      });
  },

  getAllCategoryPlaces: async (categoryKey: string): Promise<Place[]> => {
    const requestURL = constructGetAllCategoryPlacesURL(categoryKey);

    try {
      const response = await axios.get<Place[]>(requestURL);
      return response.data;
    } catch (error) {
      console.error("Error fetching category places:", error);
      return [];
    }
  },

  getAllOmittedCategories: async (): Promise<GoogleCategory[]> => {
    const requestURL = constructGetOmittedCategoriesURL();
    const response = await axios.get<GoogleCategory[]>(requestURL);
    return response.data;
  },

  getAllOmmitedCategoryBusinesses: async (): Promise<Place[]> => {
    const requestURL = constructGetOmmitedCategoryBusinessesURL();
    const response = await axios.get<Place[]>(requestURL);
    return response.data;
  },

  connectGoogleCategoryToLocalCategory: async (categoryData: {
    localCategoryKey: string;
    googleCategoryId: string;
  }) => {
    const requestURL =
      constructConnectGoogleCategoryToLocalCategoryURL(categoryData);
    const response = await axios.post(requestURL);
    return response.data;
  },

  removeCategory: async (googleCategoryTypeName: string) => {
    const requestURL = constructRemoveCategoryURL(googleCategoryTypeName);
    const response = await axios.post(requestURL);
    return response.data;
  },

  getPlaceDetails: async (placeId: string) => {
    const requestURL = constructGetPlaceDetailsURL(placeId);
    const response = await axios.get(requestURL);
    return response.data;
  },

  omitPlace: async (placeId: string) => {
    console.log("Omitting place:", placeId);
    const requestURL = constructOmitPlaceURL(placeId);
    const response = await axios.patch(requestURL);
    return response.data;
  },
};

export default AllapattahLocalGuideService;
